import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  useGetAppStoreDataQuery,
  useLazyGetAppStoreDataQuery
} from '../../redux/apis/linkManagement/app/data/lmAppsDataApiSlice'
import { toast } from 'react-toastify'
import Loader from '../pages/loader'
import ReusableTable from '../../components/other/ReuseableTable'
import EditCreateAppData from '../../components/app/EditCreateAppData'
import { Link } from 'react-router-dom'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import AppsDropdown from '../../components/AppsDropdown'
import AppStatusDropdown from '../../components/AppStatusDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'
import { can } from '../../helpers/auth'
import AssigneeDropdown from '../../components/AssigneeDropdown'

const AppData = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const context = useContext(AppSettings)

  const { data, isLoading, error, isFetching } = useGetAppStoreDataQuery({
    expand: [
      'last_updated_by',
      'found_by',
      'app',
      'app_store',
      'status',
      'customers'
    ].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    ...(search ? { search } : {}),
    ...filters
  })

  const [holder, setHolder] = useState()
  const [edit, setEdit] = useState(false)
  const [create, setCreate] = useState(false)

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'STORE NAME',
      dataIndex: 'app_store',
      key: 'app_store.app_store_name',
      filter: true,
      filterType: 'text',
      render: text => <div>{text?.app_store_name}</div>
    },
    {
      title: 'APP NAME',
      dataIndex: 'app',
      key: 'app.app_name',
      filter: true,
      filterComponent: AppsDropdown,
      render: text => <div>{text?.app_name}</div>
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      filter: true,
      filterType: 'text',
      width: 200,
      ellipsis: true,
      render: text => <div className='truncate max-w-[200px]'>{text}</div>
    },
    {
      title: 'INSTALL COUNT',
      dataIndex: 'installation_count',
      key: 'installation_count'
    },
    {
      title: 'REPORT DATE',
      dataIndex: 'report_date',
      key: 'report_date'
    },
    {
      title: 'REMOVE DATE',
      dataIndex: 'remove_date',
      key: 'remove_date'
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status.status',
      filter: true,
      filterComponent: AppStatusDropdown,
      render: text => <div>{text?.status}</div>
    },
    {
      title: 'FOUND BY',
      dataIndex: 'found_by',
      key: 'found_by',
      filter: true,
      filterComponent: AssigneeDropdown,
      props: { label: 'FOUND BY' },
      render: text => (
        <div>
          {text?.first_name} {text?.last_name}
        </div>
      )
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <div>
          {text?.first_name} {text?.last_name}
        </div>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (_, record) => (
        <div className='flex gap-2'>
          <Link className='btn btn-warning' to={`/apps/data/${record?.id}`}>
            View
          </Link>
          {can('change_appstoredata') && (
            <button
              className='btn btn-outline-theme'
              onClick={() => handleEdit(record)}
            >
              Edit
            </button>
          )}
        </div>
      )
    }
  ]

  const handleEdit = record => {
    setHolder(record)
    setEdit(true)
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => toast.error(message))
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching} />

      {can('add_appstoredata') && create && (
        <EditCreateAppData
          state='create'
          open={create}
          setOpen={setCreate}
          record={null}
        />
      )}
      {can('change_appstoredata') && edit && (
        <EditCreateAppData
          state='edit'
          open={edit}
          setOpen={setEdit}
          record={holder}
        />
      )}

      <div className='flex'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetAppStoreDataQuery}
            fileName='appData'
          />
          {can('add_appstoredata') && (
            <button
              className='btn btn-outline-theme ml-auto mb-4'
              onClick={() => setCreate(true)}
            >
              Create App Data
            </button>
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default AppData
